import React from 'react';
import clsx from 'clsx';
import { Loader2 } from 'lucide-react';

/**
 * An on/off switch with its label, standing in for MUI's `Switch` with a
 * `FormControlLabel`.
 *
 * @param {object} props
 * @param {boolean} props.checked
 * @param {(checked: boolean) => void} props.onChange - Receives the new value.
 * @param {React.ReactNode} [props.label]
 * @param {string} [props.ariaLabel] - Needed when there is no visible label, or
 *   when the label ("Activo") does not say what is being switched.
 * @param {boolean} [props.disabled=false]
 * @param {boolean} [props.loading=false] - The change is on its way to the
 *   server: a spinner in the knob, and no second click until it answers.
 * @param {string} [props.testId]
 */
export const Switch = ({
  checked,
  onChange,
  label,
  ariaLabel,
  disabled = false,
  loading = false,
  testId,
}) => (
  <label
    className={clsx(
      'inline-flex items-center gap-3 select-none',
      disabled || loading ? 'cursor-not-allowed' : 'cursor-pointer',
      disabled && 'opacity-60'
    )}
  >
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      aria-busy={loading}
      disabled={disabled || loading}
      data-testid={testId}
      onClick={() => onChange(!checked)}
      className={clsx(
        'relative w-10 h-6 rounded-full transition-colors flex-shrink-0 disabled:cursor-not-allowed',
        checked ? 'bg-ninja-green' : 'bg-border'
      )}
    >
      <span
        className={clsx(
          'absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all flex items-center justify-center',
          checked ? 'left-5' : 'left-1'
        )}
      >
        {loading && <Loader2 size={12} className="text-ninja-green animate-spin" />}
      </span>
    </button>
    {label && <span className="text-sm text-ninja-dark dark:text-white">{label}</span>}
  </label>
);
